import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { DateNav } from './components/DateNav'
import { DoneSection } from './components/DoneSection'
import { HabitDayProgress } from './components/HabitDayProgress'
import { HabitFormSheet } from './components/HabitFormSheet'
import { SortableHabitCard } from './components/SortableHabitCard'
import type { Habit, HabitFormData, HabitWithLog } from './interfaces'
import {
  getHabitDay,
  getPeriodKey,
  isReadOnlyFutureDate,
  isSameCalendarDay,
} from './utils/dateHelpers'
import { openDeeplink } from './utils/deeplink'
import {
  ALL_HABITS_CATEGORY,
  getCategoryFilterOptions,
  normalizeHabitCategory,
} from './utils/habitCategories'
import {
  createEmptyHabitLog,
  habitCacheKey,
  loadHabitsWithLogs,
  loadNextHabitSortOrder,
  normalizeHabitRow,
} from './utils/habitData'
import {
  normalizeHabitOrder,
  reorderHabitByDrop,
  sortHabitsByOrder,
} from './utils/habitOrdering'
import { updateParentWithSubHabitProgress } from './utils/subHabitProgress'
import { useDeviceMode } from './utils/useDeviceMode'

function HabitContent(): ReactElement {
  const { isDesktop } = useDeviceMode()
  const user = useAuthStore((state) => state.user)
  const profile = useAuthStore((state) => state.profile)
  const dayStartTime = (profile?.day_start_time ?? '00:00:00').slice(0, 5)
  const [habits, setHabits] = useState<HabitWithLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingHabit, setEditingHabit] = useState<HabitWithLog | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(ALL_HABITS_CATEGORY)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const realToday = useRef(new Date()).current
  const hasUserSelectedDate = useRef(false)
  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    getHabitDay(new Date(), dayStartTime),
  )

  const handleDateChange = (date: Date): void => {
    hasUserSelectedDate.current = !isSameCalendarDay(
      date,
      realToday,
      dayStartTime,
    )
    setSelectedDate(date)
  }

  useEffect(() => {
    if (!hasUserSelectedDate.current) {
      setSelectedDate(getHabitDay(new Date(), dayStartTime))
    }
  }, [dayStartTime])

  const isReadOnlyDate = isReadOnlyFutureDate(
    selectedDate,
    realToday,
    dayStartTime,
  )

  useEffect(() => {
    if (!user || habits.length === 0) return
    localStorage.setItem(
      habitCacheKey(user.id, selectedDate, dayStartTime),
      JSON.stringify(habits),
    )
  }, [habits, selectedDate, user, dayStartTime])

  useEffect(() => {
    if (!user) return

    const currentUser = user
    const cachedHabits = localStorage.getItem(
      habitCacheKey(currentUser.id, selectedDate, dayStartTime),
    )

    if (cachedHabits) {
      try {
        setHabits(
          sortHabitsByOrder(
            (JSON.parse(cachedHabits) as HabitWithLog[]).map((habit) => ({
              ...habit,
              category: normalizeHabitCategory(habit.category),
              ...normalizeHabitOrder(habit.pinned, habit.sort_order),
            })),
          ),
        )
        setIsRefreshing(true)
      } catch {
        setIsLoading(true)
      }
    } else {
      setIsLoading(true)
    }

    async function fetchHabits(date: Date): Promise<void> {
      setIsRefreshing(true)

      try {
        const loadedHabits = await loadHabitsWithLogs(
          currentUser.id,
          date,
          dayStartTime,
        )
        setHabits(loadedHabits)
        setIsLoading(false)
      } finally {
        setIsRefreshing(false)
      }
    }

    fetchHabits(selectedDate)
  }, [user, selectedDate, dayStartTime])

  useEffect(() => {
    if (
      selectedCategory !== ALL_HABITS_CATEGORY &&
      !habits.some(
        (habit) => normalizeHabitCategory(habit.category) === selectedCategory,
      )
    ) {
      setSelectedCategory(ALL_HABITS_CATEGORY)
    }
  }, [habits, selectedCategory])

  // ─── Toggle (top-level habit without sub-habits) ─────────────────────────

  async function handleToggle(habit: HabitWithLog): Promise<void> {
    if (!user) return

    if (habit.isCompletedThisPeriod && habit.logId) {
      await supabase
        .schema('habit')
        .from('habit_logs')
        .delete()
        .eq('id', habit.logId)
      setHabits((previousHabits) =>
        previousHabits.map((currentHabit) => {
          if (currentHabit.id !== habit.id) return currentHabit
          const newWeeklyCompletions =
            currentHabit.frequency_type === 'x_per_week'
              ? Math.max(0, currentHabit.weeklyCompletions - 1)
              : currentHabit.weeklyCompletions
          const newIsWeeklyTargetMet =
            currentHabit.frequency_type === 'x_per_week' &&
            newWeeklyCompletions >= (currentHabit.frequency_target ?? 1)
          return {
            ...currentHabit,
            isCompletedThisPeriod: false,
            isWeeklyTargetMet: newIsWeeklyTargetMet,
            logId: null,
            weeklyCompletions: newWeeklyCompletions,
          }
        }),
      )
      return
    }

    if (habit.deeplink_url) {
      openDeeplink(habit.deeplink_url)
    }

    const { data } = await supabase
      .schema('habit')
      .from('habit_logs')
      .upsert(
        {
          habit_id: habit.id,
          user_id: user.id,
          period_key: getPeriodKey(
            habit.frequency_type,
            selectedDate,
            dayStartTime,
          ),
          skipped: false,
        },
        { onConflict: 'habit_id,period_key' },
      )
      .select('id')
      .single()

    if (!data) return

    setHabits((previousHabits) =>
      previousHabits.map((currentHabit) => {
        if (currentHabit.id !== habit.id) return currentHabit
        const newWeeklyCompletions =
          currentHabit.frequency_type === 'x_per_week'
            ? currentHabit.weeklyCompletions + 1
            : currentHabit.weeklyCompletions
        const newIsWeeklyTargetMet =
          currentHabit.frequency_type === 'x_per_week' &&
          newWeeklyCompletions >= (currentHabit.frequency_target ?? 1)
        return {
          ...currentHabit,
          isCompletedThisPeriod: true,
          isSkippedThisPeriod: false,
          isWeeklyTargetMet: newIsWeeklyTargetMet,
          logId: data.id,
          weeklyCompletions: newWeeklyCompletions,
        }
      }),
    )
  }

  // ─── Toggle a sub-habit ───────────────────────────────────────────────────

  async function handleSubHabitToggle(
    subHabit: HabitWithLog,
    parentId: string,
  ): Promise<void> {
    if (!user) return

    if (subHabit.isCompletedThisPeriod && subHabit.logId) {
      // Un-complete: delete the log
      await supabase
        .schema('habit')
        .from('habit_logs')
        .delete()
        .eq('id', subHabit.logId)

      setHabits((prev) =>
        updateParentWithSubHabitProgress(prev, parentId, subHabit.id, {
          type: 'undo',
        }),
      )
      return
    }

    if (subHabit.deeplink_url) {
      openDeeplink(subHabit.deeplink_url)
    }

    // Complete: insert a log for the sub-habit
    const { data } = await supabase
      .schema('habit')
      .from('habit_logs')
      .upsert(
        {
          habit_id: subHabit.id,
          user_id: user.id,
          period_key: getPeriodKey(
            subHabit.frequency_type,
            selectedDate,
            dayStartTime,
          ),
          skipped: false,
        },
        { onConflict: 'habit_id,period_key' },
      )
      .select('id')
      .single()

    if (!data) return

    setHabits((prev) =>
      updateParentWithSubHabitProgress(prev, parentId, subHabit.id, {
        type: 'complete',
        logId: data.id,
      }),
    )
  }

  // ─── Create ───────────────────────────────────────────────────────────────

  // ─── Create ───────────────────────────────────────────────────────────────

  async function handleCreate(data: HabitFormData): Promise<void> {
    if (!user) return

    const sortOrder = await loadNextHabitSortOrder(user.id)

    const parentFrequencyType = data.frequency_type
    const parentFrequencyTarget = data.frequency_target
    const parentWeeklyDays =
      parentFrequencyType === 'weekly_days' ? (data.weekly_days ?? null) : null

    const { data: row } = await supabase
      .schema('habit')
      .from('habits')
      .insert({
        name: data.name,
        description: data.description,
        category: data.category,
        frequency_type: parentFrequencyType,
        frequency_target: parentFrequencyTarget,
        weekly_days: parentWeeklyDays,
        deeplink_url: data.deeplink_url?.trim() || null,
        user_id: user.id,
        sort_order: sortOrder,
      })
      .select()
      .single()

    if (!row) return

    const parentHabit = normalizeHabitRow(row as Habit)
    const parentWithLog = createEmptyHabitLog(parentHabit)

    // Insert sub-habits if any
    let subHabitsWithLog: HabitWithLog[] = []
    if (data.subHabits && data.subHabits.length > 0) {
      const { data: subRows } = await supabase
        .schema('habit')
        .from('habits')
        .insert(
          data.subHabits.map((sh, i) => ({
            name: sh.name,
            description: sh.description,
            category: data.category,
            frequency_type: sh.frequency_type,
            frequency_target:
              sh.frequency_type === 'x_per_week'
                ? (sh.frequency_target ?? 3)
                : null,
            weekly_days:
              sh.frequency_type === 'weekly_days'
                ? (sh.weekly_days ?? null)
                : null,
            deeplink_url: sh.deeplink_url?.trim() || null,
            user_id: user.id,
            sort_order: i,
            parent_habit_id: parentHabit.id,
          })),
        )
        .select()

      subHabitsWithLog = ((subRows ?? []) as Habit[]).map((subRow) =>
        createEmptyHabitLog(normalizeHabitRow(subRow)),
      )
    }

    setHabits((previousHabits) =>
      sortHabitsByOrder([
        ...previousHabits,
        { ...parentWithLog, subHabits: subHabitsWithLog },
      ]),
    )

    setShowForm(false)
  }

  // ─── Update ───────────────────────────────────────────────────────────────

  async function handleUpdate(data: HabitFormData): Promise<void> {
    if (!editingHabit) return

    const parentFrequencyType = data.frequency_type
    const parentFrequencyTarget = data.frequency_target
    const parentWeeklyDays =
      parentFrequencyType === 'weekly_days' ? (data.weekly_days ?? null) : null

    const { data: row } = await supabase
      .schema('habit')
      .from('habits')
      .update({
        name: data.name,
        description: data.description,
        category: data.category,
        frequency_type: parentFrequencyType,
        frequency_target: parentFrequencyTarget ?? null,
        weekly_days: parentWeeklyDays,
        deeplink_url: data.deeplink_url?.trim() || null,
      })
      .eq('id', editingHabit.id)
      .select()
      .single()

    if (!row) return

    const updatedParent = normalizeHabitRow(row as Habit)

    // Diff sub-habits: upsert existing/new, delete removed
    const incomingSubHabits = data.subHabits ?? []
    const existingSubHabits = editingHabit.subHabits ?? []
    const incomingIds = new Set(
      incomingSubHabits.filter((sh) => sh.id).map((sh) => sh.id as string),
    )

    // Delete sub-habits that were removed
    const toDelete = existingSubHabits.filter((sh) => !incomingIds.has(sh.id))
    if (toDelete.length > 0) {
      await supabase
        .schema('habit')
        .from('habits')
        .delete()
        .in(
          'id',
          toDelete.map((sh) => sh.id),
        )
    }

    // Upsert remaining sub-habits
    const toUpsert = incomingSubHabits.map((sh, i) => ({
      id: sh.id || crypto.randomUUID(),
      name: sh.name,
      description: sh.description,
      category: data.category,
      frequency_type: sh.frequency_type,
      frequency_target:
        sh.frequency_type === 'x_per_week' ? (sh.frequency_target ?? 3) : null,
      weekly_days:
        sh.frequency_type === 'weekly_days' ? (sh.weekly_days ?? null) : null,
      deeplink_url: sh.deeplink_url?.trim() || null,
      user_id: editingHabit.user_id,
      sort_order: i,
      parent_habit_id: editingHabit.id,
    }))

    let updatedSubHabitsWithLog: HabitWithLog[] = []
    if (toUpsert.length > 0) {
      const { data: subRows } = await supabase
        .schema('habit')
        .from('habits')
        .upsert(toUpsert, { onConflict: 'id' })
        .select()

      // Preserve existing log state for sub-habits that already existed
      const existingSubHabitById = new Map(
        existingSubHabits.map((sh) => [sh.id, sh]),
      )
      updatedSubHabitsWithLog = ((subRows ?? []) as Habit[]).map((subRow) => {
        const existing = existingSubHabitById.get(subRow.id)
        const base = createEmptyHabitLog(normalizeHabitRow(subRow))
        return existing
          ? {
              ...base,
              isCompletedThisPeriod: existing.isCompletedThisPeriod,
              isSkippedThisPeriod: existing.isSkippedThisPeriod,
              isWeeklyTargetMet: existing.isWeeklyTargetMet,
              logId: existing.logId,
              currentStreak: existing.currentStreak,
              bestStreak: existing.bestStreak,
              weeklyCompletions: existing.weeklyCompletions,
            }
          : base
      })
    }

    const hasSubHabits = updatedSubHabitsWithLog.length > 0
    const newParentWeekly = hasSubHabits
      ? updatedSubHabitsWithLog.reduce(
          (sum, sh) => sum + sh.weeklyCompletions,
          0,
        )
      : editingHabit.weeklyCompletions

    const parentTarget = updatedParent.frequency_target ?? 1
    const isCompletedThisPeriod = hasSubHabits
      ? updatedSubHabitsWithLog.every((sh) => sh.isCompletedThisPeriod)
      : editingHabit.isCompletedThisPeriod

    const isWeeklyTargetMet = hasSubHabits
      ? newParentWeekly >= parentTarget
      : updatedParent.frequency_type === 'x_per_week'
        ? newParentWeekly >= parentTarget
        : editingHabit.isWeeklyTargetMet

    setHabits((previousHabits) =>
      previousHabits.map((habit) =>
        habit.id === editingHabit.id
          ? {
              ...habit,
              name: updatedParent.name,
              description: updatedParent.description ?? null,
              category: updatedParent.category,
              frequency_type: updatedParent.frequency_type,
              frequency_target: updatedParent.frequency_target ?? null,
              weekly_days: updatedParent.weekly_days ?? null,
              updated_at: updatedParent.updated_at,
              subHabits: updatedSubHabitsWithLog,
              weeklyCompletions: newParentWeekly,
              isCompletedThisPeriod,
              isWeeklyTargetMet,
            }
          : habit,
      ),
    )

    setEditingHabit(null)
  }

  // ─── Toggle pinned ────────────────────────────────────────────────────────

  async function handleTogglePinned(habit: HabitWithLog): Promise<void> {
    const pinned = !habit.pinned

    await supabase
      .schema('habit')
      .from('habits')
      .update({ pinned })
      .eq('id', habit.id)

    setHabits((previousHabits) =>
      sortHabitsByOrder(
        previousHabits.map((currentHabit) =>
          currentHabit.id === habit.id
            ? { ...currentHabit, pinned }
            : currentHabit,
        ),
      ),
    )
  }

  // ─── Reorder ──────────────────────────────────────────────────────────────

  async function handleReorderHabits(
    activeHabitId: string,
    overHabitId: string,
    scopedHabitIds: string[],
  ): Promise<void> {
    const reorderedHabits = reorderHabitByDrop(
      habits,
      activeHabitId,
      overHabitId,
      scopedHabitIds,
    )
    const sortOrderByHabitId = new Map(
      habits.map((currentHabit) => [currentHabit.id, currentHabit.sort_order]),
    )
    const changedHabits = reorderedHabits.filter(
      (currentHabit) =>
        sortOrderByHabitId.get(currentHabit.id) !== currentHabit.sort_order,
    )

    if (changedHabits.length === 0) return

    await Promise.all(
      changedHabits.map((currentHabit) =>
        supabase
          .schema('habit')
          .from('habits')
          .update({ sort_order: currentHabit.sort_order })
          .eq('id', currentHabit.id),
      ),
    )

    setHabits(reorderedHabits)
  }

  // ─── Skip ─────────────────────────────────────────────────────────────────

  async function handleSkip(habit: HabitWithLog): Promise<void> {
    if (!user || isReadOnlyDate) return

    if (habit.isSkippedThisPeriod && habit.logId) {
      await supabase
        .schema('habit')
        .from('habit_logs')
        .delete()
        .eq('id', habit.logId)
      setHabits((previousHabits) =>
        previousHabits.map((currentHabit) => {
          if (currentHabit.id !== habit.id) return currentHabit
          const newIsWeeklyTargetMet =
            currentHabit.frequency_type === 'x_per_week' &&
            currentHabit.weeklyCompletions >=
              (currentHabit.frequency_target ?? 1)
          return {
            ...currentHabit,
            isCompletedThisPeriod: false,
            isSkippedThisPeriod: false,
            isWeeklyTargetMet: newIsWeeklyTargetMet,
            logId: null,
          }
        }),
      )
      return
    }

    const { data } = await supabase
      .schema('habit')
      .from('habit_logs')
      .upsert(
        {
          habit_id: habit.id,
          user_id: user.id,
          period_key: getPeriodKey(
            habit.frequency_type,
            selectedDate,
            dayStartTime,
          ),
          skipped: true,
        },
        { onConflict: 'habit_id,period_key' },
      )
      .select('id')
      .single()

    if (!data) return

    setHabits((previousHabits) =>
      previousHabits.map((currentHabit) => {
        if (currentHabit.id !== habit.id) return currentHabit
        const hadTodayCompletion =
          habit.logId !== null && !habit.isSkippedThisPeriod
        const newWeeklyCompletions =
          currentHabit.frequency_type === 'x_per_week' && hadTodayCompletion
            ? Math.max(0, currentHabit.weeklyCompletions - 1)
            : currentHabit.weeklyCompletions
        const newIsWeeklyTargetMet =
          currentHabit.frequency_type === 'x_per_week' &&
          newWeeklyCompletions >= (currentHabit.frequency_target ?? 1)
        return {
          ...currentHabit,
          isSkippedThisPeriod: true,
          isCompletedThisPeriod: false,
          isWeeklyTargetMet: newIsWeeklyTargetMet,
          logId: data.id,
          weeklyCompletions: newWeeklyCompletions,
        }
      }),
    )
  }

  // ─── Drag end ─────────────────────────────────────────────────────────────

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event

    if (!over) return

    const activeHabitId = String(active.id)
    const overHabitId = String(over.id)

    const incompleteHabitIds = incompleteHabits.map((h) => h.id)
    const completedHabitIds = completedHabits.map((h) => h.id)

    let scopedHabitIds: string[] | null = null
    if (
      incompleteHabitIds.includes(activeHabitId) &&
      incompleteHabitIds.includes(overHabitId)
    ) {
      scopedHabitIds = incompleteHabitIds
    } else if (
      completedHabitIds.includes(activeHabitId) &&
      completedHabitIds.includes(overHabitId)
    ) {
      scopedHabitIds = completedHabitIds
    }

    if (!scopedHabitIds) return

    await handleReorderHabits(activeHabitId, overHabitId, scopedHabitIds)
  }

  // ─── Derived state ────────────────────────────────────────────────────────

  const categoryOptions = getCategoryFilterOptions(habits)
  const visibleHabits =
    selectedCategory === ALL_HABITS_CATEGORY
      ? habits
      : habits.filter(
          (habit) =>
            normalizeHabitCategory(habit.category) === selectedCategory,
        )
  const incompleteHabits = visibleHabits.filter(
    (habit) => !habit.isCompletedThisPeriod,
  )
  const completedHabits = visibleHabits.filter(
    (habit) => habit.isCompletedThisPeriod,
  )

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className={`relative mx-auto px-4 transition-all duration-200 ${
        isDesktop ? 'max-w-6xl py-6 pb-16' : 'max-w-lg pt-4 pb-safe-24'
      }`}
    >
      {isRefreshing && (
        <div className="-translate-x-1/2 pointer-events-none absolute top-4 left-1/2 z-10 rounded-full bg-white/90 px-3 py-2 shadow-lg ring-1 ring-black/10 backdrop-blur-sm">
          <div className="flex items-center gap-2 font-medium text-gray-700 text-sm">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Refreshing…
          </div>
        </div>
      )}

      {isDesktop ? (
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <DateNav
              selectedDate={selectedDate}
              realToday={realToday}
              onChange={handleDateChange}
              dayStartTime={dayStartTime}
              isDesktop={isDesktop}
            />

            <div className="flex flex-wrap items-center gap-2.5">
              <Link
                to="/settings"
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 font-medium text-gray-700 text-sm shadow-xs hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-500" />
                Settings
              </Link>
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 font-semibold text-sm text-white shadow-sm hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5" />
                New Habit
              </button>
            </div>
          </div>

          <HabitDayProgress
            completedCount={completedHabits.length}
            totalCount={visibleHabits.length}
            isDesktop={isDesktop}
          />
        </div>
      ) : (
        <div className="mb-4">
          <DateNav
            selectedDate={selectedDate}
            realToday={realToday}
            onChange={handleDateChange}
            dayStartTime={dayStartTime}
            isDesktop={isDesktop}
          />
          <HabitDayProgress
            completedCount={completedHabits.length}
            totalCount={visibleHabits.length}
            isDesktop={isDesktop}
          />
        </div>
      )}

      {habits.length > 0 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          {categoryOptions.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              className={`shrink-0 rounded-full px-3 py-1.5 font-medium text-sm transition-colors ${
                selectedCategory === category
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {isReadOnlyDate && (
        <div className="mb-4 rounded-xl bg-amber-50 px-3.5 py-2 text-amber-700 text-xs">
          Viewing a future date — habit completion is read-only.
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {isLoading ? (
          <div
            className={
              isDesktop
                ? 'mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2'
                : 'mb-6 flex flex-col gap-3'
            }
          >
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-20 animate-pulse rounded-2xl bg-gray-100"
              />
            ))}
          </div>
        ) : isDesktop ? (
          <div className="mb-8 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            {/* Column 1: To Do */}
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white/70 p-4 shadow-xs">
              <div className="flex items-center justify-between border-gray-100 border-b pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base" aria-hidden="true">
                    📋
                  </span>
                  <h2 className="font-semibold text-gray-900 text-sm">To Do</h2>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 text-xs">
                    {incompleteHabits.length}
                  </span>
                </div>
              </div>

              {incompleteHabits.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 border-dashed bg-gray-50/50 px-4 py-12 text-center">
                  {habits.length === 0 ? (
                    <>
                      <p className="font-medium text-gray-700 text-sm">
                        No habits yet
                      </p>
                      <p className="mt-1 text-gray-400 text-xs">
                        Click &quot;New Habit&quot; above to create one.
                      </p>
                    </>
                  ) : completedHabits.length > 0 ? (
                    <>
                      <span className="text-3xl" aria-hidden="true">
                        🎉
                      </span>
                      <p className="mt-2 font-medium text-emerald-800 text-sm">
                        All habits completed!
                      </p>
                      <p className="mt-1 text-emerald-600 text-xs">
                        You have completed all habits for this date.
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      No habits in this category.
                    </p>
                  )}
                </div>
              ) : (
                <SortableContext
                  items={incompleteHabits.map((habit) => habit.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-3">
                    {incompleteHabits.map((habit) => (
                      <SortableHabitCard
                        key={habit.id}
                        habit={habit}
                        isReadOnlyDate={isReadOnlyDate}
                        isFutureDate={isReadOnlyDate}
                        selectedDate={selectedDate}
                        dayStartTime={dayStartTime}
                        onToggle={handleToggle}
                        onEdit={(selectedHabit) =>
                          setEditingHabit(selectedHabit)
                        }
                        onSkip={handleSkip}
                        onTogglePinned={handleTogglePinned}
                        onSubHabitToggle={handleSubHabitToggle}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </div>

            {/* Column 2: Completed */}
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white/70 p-4 shadow-xs">
              <div className="flex items-center justify-between border-gray-100 border-b pb-3">
                <div className="flex items-center gap-2">
                  <span className="text-base" aria-hidden="true">
                    ✅
                  </span>
                  <h2 className="font-semibold text-gray-900 text-sm">
                    Completed
                  </h2>
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800 text-xs">
                    {completedHabits.length}
                  </span>
                </div>
              </div>

              <DoneSection
                completedHabits={completedHabits}
                isReadOnlyDate={isReadOnlyDate}
                isFutureDate={isReadOnlyDate}
                selectedDate={selectedDate}
                dayStartTime={dayStartTime}
                onToggle={handleToggle}
                onEdit={(selectedHabit) => setEditingHabit(selectedHabit)}
                onSkip={handleSkip}
                onTogglePinned={handleTogglePinned}
                onSubHabitToggle={handleSubHabitToggle}
                isDesktop={true}
              />
            </div>
          </div>
        ) : (
          /* Mobile Single-Column Layout */
          <>
            {incompleteHabits.length === 0 && completedHabits.length === 0 ? (
              <div className="mb-6 flex items-center gap-2 rounded-2xl border border-gray-200 border-dashed px-4 py-5 text-gray-400">
                <p className="text-sm">
                  {habits.length === 0
                    ? 'No habits yet. Tap + to add one.'
                    : 'No habits in this category.'}
                </p>
              </div>
            ) : (
              <SortableContext
                items={incompleteHabits.map((habit) => habit.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="mb-6 flex flex-col gap-3">
                  {incompleteHabits.map((habit) => (
                    <SortableHabitCard
                      key={habit.id}
                      habit={habit}
                      isReadOnlyDate={isReadOnlyDate}
                      isFutureDate={isReadOnlyDate}
                      selectedDate={selectedDate}
                      dayStartTime={dayStartTime}
                      onToggle={handleToggle}
                      onEdit={(selectedHabit) => setEditingHabit(selectedHabit)}
                      onSkip={handleSkip}
                      onTogglePinned={handleTogglePinned}
                      onSubHabitToggle={handleSubHabitToggle}
                    />
                  ))}
                </div>
              </SortableContext>
            )}

            {user && (
              <DoneSection
                completedHabits={completedHabits}
                isReadOnlyDate={isReadOnlyDate}
                isFutureDate={isReadOnlyDate}
                selectedDate={selectedDate}
                dayStartTime={dayStartTime}
                onToggle={handleToggle}
                onEdit={(selectedHabit) => setEditingHabit(selectedHabit)}
                onSkip={handleSkip}
                onTogglePinned={handleTogglePinned}
                onSubHabitToggle={handleSubHabitToggle}
                isDesktop={false}
              />
            )}
          </>
        )}
      </DndContext>

      {!isDesktop && (
        <>
          <div className="mt-4 mb-2 flex justify-center">
            <Link
              to="/settings"
              className="flex items-center gap-1.5 rounded-xl bg-gray-100 px-3 py-1.5 font-medium text-gray-600 text-sm hover:bg-gray-200"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              Settings
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(true)}
            aria-label="Add habit"
            className="fixed right-5 bottom-safe-5 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg active:bg-indigo-700"
          >
            <PlusIcon className="h-7 w-7" />
          </button>
        </>
      )}

      {showForm && (
        <HabitFormSheet
          initial={null}
          onSubmit={handleCreate}
          onClose={() => setShowForm(false)}
        />
      )}

      {editingHabit && (
        <HabitFormSheet
          initial={{
            name: editingHabit.name,
            description: editingHabit.description ?? '',
            category: editingHabit.category,
            frequency_type: editingHabit.frequency_type,
            frequency_target: editingHabit.frequency_target ?? undefined,
            weekly_days: editingHabit.weekly_days ?? undefined,
            deeplink_url: editingHabit.deeplink_url ?? undefined,
            subHabits: (editingHabit.subHabits ?? []).map((sh) => ({
              id: sh.id,
              name: sh.name,
              description: sh.description ?? '',
              frequency_type: sh.frequency_type,
              frequency_target: sh.frequency_target ?? undefined,
              weekly_days: sh.weekly_days ?? undefined,
              deeplink_url: sh.deeplink_url ?? undefined,
            })),
          }}
          onSubmit={handleUpdate}
          onClose={() => setEditingHabit(null)}
        />
      )}
    </div>
  )
}

export default function HabitPage(): ReactElement {
  return (
    <UserGuard>
      <div className="min-h-screen bg-gray-50">
        <HabitContent />
      </div>
    </UserGuard>
  )
}
